// src/pages/AchievementsPage/AchievementsPage.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PartyPopper,
  Flame,
  Heart,
  Star,
  Sprout,
  Moon,
  Crown,
  Gem,
  Share2,
  Gift,
  MessageCircle,
  Sparkles,
  Trophy,
  Calendar,
} from 'lucide-react';
import { ACHIEVEMENTS, type Achievement } from '../../data/achievementsData';
import {
  Skeleton,
  SkeletonCard,
  SkeletonRow,
  SkeletonCircle,
  SkeletonText,
} from '../../components/ui/Skeleton';
import { SPACING, CONTAINER_PADDING, CONTAINER_PADDING_SMALL } from '../../utils/constants';
import { useRetinolStore } from '../../store/retinolStore';

import type { LucideIcon } from 'lucide-react';

// ============= ICON MAP =============
const ICON_MAP: Record<string, LucideIcon> = {
  PartyPopper,
  Flame,
  Heart,
  Star,
  Sprout,
  Moon,
  Crown,
  Gem,
  Share2,
  Gift,
  MessageCircle,
  Sparkles,
  Trophy,
};

// ============= STYLED COMPONENTS =============

const Container = styled.div`
  padding: ${CONTAINER_PADDING.top}px ${CONTAINER_PADDING.horizontal}px ${CONTAINER_PADDING.bottom}px;
  max-width: 430px;
  margin: 0 auto;
  min-height: var(--tg-viewport-stable-height, 100vh);
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 104, 238, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(135, 206, 235, 0.08) 0%, transparent 50%),
    #ffffff;

  [data-theme="dark"] & {
    background:
      radial-gradient(circle at 20% 20%, rgba(196, 181, 253, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(160, 216, 240, 0.12) 0%, transparent 50%),
      #1a1a2e;
  }

  @media (max-width: 375px) {
    padding: ${CONTAINER_PADDING_SMALL.top}px ${CONTAINER_PADDING_SMALL.horizontal}px ${CONTAINER_PADDING_SMALL.bottom}px;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: ${SPACING.section}px;

  @media (max-width: 375px) {
    margin-bottom: ${SPACING.card}px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const TrophyIconWrapper = styled.div`
  color: #7B68EE;
  display: flex;
  align-items: center;
  justify-content: center;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const Title = styled.h1`
  font-size: clamp(22px, 5vw, 26px);
  font-weight: 800;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  letter-spacing: -0.5px;

  [data-theme="dark"] & {
    /* ✅ FIX: Убираем gradient text в темной теме - используем обычный цвет */
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    color: #C4B5FD;
  }

  @media (max-width: 375px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: rgba(0,0,0,0.65);
  margin: 0;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 375px) {
    font-size: 14px;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  margin: -8px 0 ${SPACING.card}px 0;
`;

const ToggleAllHints = styled.button<{ $active: boolean }>`
  border: 1px solid rgba(123, 104, 238, 0.35);
  background: ${p => p.$active ? 'linear-gradient(135deg, rgba(123,104,238,0.15), rgba(153,102,204,0.15))' : 'transparent'};
  color: #7B68EE !important;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  [data-theme="dark"] & {
    border: 1px solid rgba(196, 181, 253, 0.35);
    background: ${p => p.$active ? 'linear-gradient(135deg, rgba(196,181,253,0.15), rgba(216,180,254,0.15))' : 'transparent'};
    color: #C4B5FD !important;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

const ProgressSection = styled(motion.div)`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.2);
  border-radius: 20px;
  padding: ${SPACING.card}px;
  margin-bottom: ${SPACING.card}px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.98),
      rgba(32, 32, 50, 0.95)
    );
    border: 1px solid rgba(196, 181, 253, 0.2);
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &::before {
      background: linear-gradient(90deg, #C4B5FD, #A0D8F0);
    }
  }

  @media (max-width: 375px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  @media (max-width: 375px) {
    margin-bottom: ${SPACING.element}px;
  }
`;

const ProgressTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

const ProgressValue = styled.div`
  font-size: clamp(16px, 4vw, 18px);
  font-weight: 800;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  [data-theme="dark"] & {
    /* ✅ FIX: Убираем gradient text в темной теме - используем обычный цвет */
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    color: #C4B5FD;
  }
`;

const CircularProgress = styled.div`
  position: relative;
  width: min(100px, 25vw);
  height: min(100px, 25vw);
  margin: 0 auto 16px;

  @media (max-width: 375px) {
    width: 90px;
    height: 90px;
  }
`;

const CircularSvg = styled.svg`
  transform: rotate(-90deg);
`;

const CircularBg = styled.circle`
  fill: none;
  stroke: rgba(123, 104, 238, 0.1);
  stroke-width: 8;
`;

const CircularBar = styled(motion.circle)`
  fill: none;
  stroke: url(#gradient);
  stroke-width: 8;
  stroke-linecap: round;
`;

const CircularText = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
`;

const CircularPercent = styled.div`
  font-size: clamp(24px, 6vw, 28px);
  font-weight: 800;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 4px;

  [data-theme="dark"] & {
    /* ✅ FIX: Убираем gradient text в темной теме - используем обычный цвет */
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    color: #C4B5FD;
  }

  @media (max-width: 375px) {
    font-size: 24px;
  }
`;

const CircularLabel = styled.div`
  font-size: clamp(10px, 2.5vw, 11px);
  color: rgba(0,0,0,0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 375px) {
    font-size: 10px;
  }
`;

const ProgressText = styled.p`
  font-size: 14px;
  color: rgba(0,0,0,0.7);
  margin: 0;
  text-align: center;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 375px) {
    font-size: 13px;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: ${SPACING.tiny}px;
  margin-bottom: ${SPACING.card}px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 375px) {
    gap: 6px;
    margin-bottom: 16px;
  }
`;

const CategoryTab = styled(motion.button)<{ $active: boolean }>`
  padding: 12px 20px;
  border-radius: 14px;
  border: none;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)'
    : 'rgba(123, 104, 238, 0.1)'
  };
  color: ${props => props.$active ? '#FFFFFF !important' : '#7B68EE !important'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;
  min-height: 44px;

  [data-theme="dark"] & {
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #C4B5FD, #D8B4FE)'
      : 'rgba(196, 181, 253, 0.1)'
    };
    color: ${props => props.$active ? '#000000 !important' : '#C4B5FD !important'};

    &:hover {
      box-shadow: 0 4px 12px rgba(196, 181, 253, 0.3);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(123, 104, 238, 0.3);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  @media (max-width: 375px) {
    padding: 8px 16px;
    font-size: 13px;
  }
`;

const AchievementsGrid = styled.div`
  display: grid;
  gap: 14px;
  margin-bottom: ${SPACING.section}px;

  @media (max-width: 375px) {
    gap: ${SPACING.element}px;
  }
`;

const AchievementCard = styled(motion.div)<{ $unlocked: boolean }>`
  background: ${props => props.$unlocked
    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.1))'
    : 'linear-gradient(135deg, rgba(148, 163, 184, 0.1), rgba(148, 163, 184, 0.05))'
  };
  border: 2px solid ${props => props.$unlocked
    ? 'rgba(34, 197, 94, 0.3)'
    : 'rgba(148, 163, 184, 0.2)'
  };
  border-radius: 20px;
  padding: 18px;
  opacity: ${props => props.$unlocked ? 1 : 0.95};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.$unlocked
      ? '0 12px 32px rgba(34, 197, 94, 0.2)'
      : '0 12px 32px rgba(148, 163, 184, 0.15)'
    };
  }

  ${props => props.$unlocked && `
    &::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: linear-gradient(90deg, #22C55E, #10B981);
    }
  `}

  [data-theme="dark"] & {
    background: ${props => props.$unlocked
      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.15))'
      : 'linear-gradient(135deg, rgba(100, 116, 139, 0.15), rgba(100, 116, 139, 0.1))'
    };
    border: 2px solid ${props => props.$unlocked
      ? 'rgba(34, 197, 94, 0.4)'
      : 'rgba(148, 163, 184, 0.3)'
    };

    &:hover {
      box-shadow: ${props => props.$unlocked
        ? '0 12px 32px rgba(34, 197, 94, 0.3)'
        : '0 12px 32px rgba(0, 0, 0, 0.4)'
      };
    }
  }

  @media (max-width: 375px) {
    padding: 16px;
    border-radius: 16px;
  }
`;

const AchievementHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: ${SPACING.element}px;

  @media (max-width: 375px) {
    gap: ${SPACING.element}px;
  }
`;

const AchievementIcon = styled.div<{ $unlocked: boolean }>`
  width: min(56px, 15vw);
  height: min(56px, 15vw);
  border-radius: 16px;
  background: ${props => props.$unlocked
    ? 'linear-gradient(135deg, #22C55E, #10B981)'
    : 'rgba(148, 163, 184, 0.2)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${props => props.$unlocked
    ? '0 4px 16px rgba(34, 197, 94, 0.3)'
    : 'none'
  };
  color: ${props => props.$unlocked ? '#ffffff !important' : 'rgba(148, 163, 184, 0.6) !important'};
  filter: ${props => props.$unlocked ? 'none' : 'grayscale(100%)'};

  svg {
    color: inherit !important;
  }

  @media (max-width: 375px) {
    width: 48px;
    height: 48px;
  }
`;

const AchievementInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AchievementName = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #000;
  margin: 0 0 4px 0;
  line-height: 1.2;

  [data-theme="dark"] & {
    color: #ffffff;
  }

  @media (max-width: 375px) {
    font-size: 14px;
  }
`;

const AchievementDescription = styled.p`
  font-size: 13px;
  color: rgba(0,0,0,0.7);
  margin: 0;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 375px) {
    font-size: 12px;
  }
`;

const HowToUnlock = styled(motion.div)<{ $unlocked: boolean }>`
  margin-top: 8px;
  padding: 10px 12px;
  background: ${props => props.$unlocked ? 'rgba(34, 197, 94, 0.08)' : 'rgba(123, 104, 238, 0.08)'};
  border-radius: 10px;
  border-left: 3px solid ${props => props.$unlocked ? '#22C55E' : '#7B68EE'};
  font-size: 12px;
  color: rgba(0,0,0,0.78) !important;
  line-height: 1.5;
  font-style: italic;
  overflow: hidden;

  * {
    color: rgba(0,0,0,0.78) !important;
  }

  [data-theme="dark"] & {
    background: ${props => props.$unlocked ? 'rgba(34, 197, 94, 0.12)' : 'rgba(196, 181, 253, 0.12)'};
    border-left: 3px solid ${props => props.$unlocked ? '#22C55E' : '#C4B5FD'};
    color: rgba(255, 255, 255, 0.85) !important;

    * {
      color: rgba(255, 255, 255, 0.85) !important;
    }
  }
`;

const HowToLabel = styled.span`
  font-weight: 700;
  color: #7B68EE !important;
  font-style: normal;
  margin-right: 6px;

  [data-theme="dark"] & {
    color: #C4B5FD !important;
  }
`;

const UnlockedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: linear-gradient(135deg, #22C55E, #10B981);
  color: white !important;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);

  @media (max-width: 375px) {
    padding: 3px 8px;
    font-size: 9px;
  }
`;

const UnlockedDate = styled.div`
  font-size: 11px;
  color: rgba(0,0,0,0.5);
  margin-top: ${SPACING.tiny}px;
  font-style: italic;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 375px) {
    font-size: 10px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px 20px;

  @media (max-width: 375px) {
    padding: 40px 16px;
  }
`;

const EmptyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: rgba(123, 104, 238, 0.5);

  [data-theme="dark"] & {
    color: rgba(196, 181, 253, 0.5);
  }
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 8px 0;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: rgba(0,0,0,0.7);
  margin: 0 0 20px 0;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const EmptyButton = styled(motion.button)`
  padding: 14px 28px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white !important;
  border: none;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.tiny}px;
  box-shadow: 0 4px 12px rgba(123, 104, 238, 0.3);

  svg {
    color: white !important;
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #C4B5FD, #D8B4FE);
    color: #000000 !important;
    box-shadow: 0 4px 12px rgba(196, 181, 253, 0.3);

    svg {
      color: #000000 !important;
    }

    &:hover {
      box-shadow: 0 8px 24px rgba(196, 181, 253, 0.4);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(123, 104, 238, 0.4);
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const HintButton = styled.button`
  border: none;
  background: rgba(123, 104, 238, 0.1);
  color: #7B68EE !important;
  font-weight: 700;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;

  [data-theme="dark"] & {
    background: rgba(196, 181, 253, 0.1);
    color: #C4B5FD !important;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

// ============= COMPONENTS =============

const AchievementCardItem = React.memo<{
  achievement: Achievement;
  index: number;
  showHintsGlobally: boolean;
}>(({ achievement, index, showHintsGlobally }) => {
  const[open, setOpen] = React.useState(false);
  const isOpen = showHintsGlobally || open;

  const IconComponent = ICON_MAP[achievement.icon] || Star;

  return (
    <AchievementCard
      $unlocked={achievement.unlocked}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      layout
    >
      {achievement.unlocked && <UnlockedBadge>Открыто ✓</UnlockedBadge>}

      <AchievementHeader>
        <AchievementIcon $unlocked={achievement.unlocked}>
          <IconComponent size={28} />
        </AchievementIcon>
        <AchievementInfo>
          <AchievementName>{achievement.name}</AchievementName>
          <AchievementDescription>{achievement.description}</AchievementDescription>

          <CardActions>
            {!achievement.unlocked && (
              <HintButton
                aria-expanded={isOpen}
                aria-controls={`howto-${achievement.id}`}
                onClick={() => setOpen((v) => !v)}
              >
                {isOpen ? 'Скрыть инструкцию' : 'Как получить?'}
              </HintButton>
            )}
          </CardActions>

          <AnimatePresence initial={false}>
            {(isOpen || achievement.unlocked) && (
              <HowToUnlock
                id={`howto-${achievement.id}`}
                $unlocked={achievement.unlocked}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {achievement.unlocked ? (
                  <>
                    <HowToLabel>✅ Выполнено:</HowToLabel>
                    {achievement.howToUnlock}
                  </>
                ) : (
                  <>
                    <HowToLabel>💡 Как получить:</HowToLabel>
                    {achievement.howToUnlock}
                  </>
                )}
              </HowToUnlock>
            )}
          </AnimatePresence>
        </AchievementInfo>
      </AchievementHeader>

      {achievement.unlocked && achievement.unlockedAt && (
        <UnlockedDate>
          Открыто{' '}
          {new Date(achievement.unlockedAt).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </UnlockedDate>
      )}
    </AchievementCard>
  );
});
AchievementCardItem.displayName = 'AchievementCardItem';

// ============= PAGE =============

export default function AchievementsPage() {
  const navigate = useNavigate();
  const[category, setCategory] = useState<'all' | 'unlocked' | 'locked'>('unlocked');
  const[isLoading, setIsLoading] = useState(true);
  const[showHints, setShowHints] = useState(false);

  // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ: Используем achievementsCache из retinolStore вместо локального состояния
  const { checkAndUnlockAchievements, achievementsCache } = useRetinolStore((state) => ({
    checkAndUnlockAchievements: state.checkAndUnlockAchievements,
    achievementsCache: state.achievementsCache,
  }));

  // ✅ Используем achievementsCache из store (синхронизировано с ProfilePage)
  const achievements = achievementsCache || ACHIEVEMENTS.map(a => ({ ...a, unlocked: false }));

  // ✅ КРИТИЧНО: Гидратация при монтировании + focus + visibilitychange
  useEffect(() => {
    let alive = true;
    setIsLoading(true);

    // Функция гидратации с forceReload
    const hydrate = async (force = false) => {
      if (!alive) return;

      try {
        // ✅ forceReload = true → всегда загружаем из CloudStorage
        await checkAndUnlockAchievements(force);
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Гидратация завершена${force ? ' (force)' : ''}`);
        }
      } catch (error) {
        console.warn('⚠️ Ошибка гидратации достижений:', error);
      } finally {
        if (alive && !force) {
          setIsLoading(false);
        }
      }
    };

    // Начальная гидратация
    hydrate(false);

    // ✅ НОВОЕ: слушатели для синхронизации между устройствами
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (process.env.NODE_ENV === 'development') {
          console.log('👁️ Страница стала видимой → гидратация');
        }
        hydrate(true); // forceReload
      }
    };

    const handleFocus = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Окно получило фокус → гидратация');
      }
      hydrate(true); // forceReload
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAndUnlockAchievements]); // Зависимость от checkAndUnlockAchievements

  const stats = useMemo(() => {
    const total = achievements.length;
    const unlocked = achievements.filter((a) => a.unlocked).length;
    const percent = total ? Math.round((unlocked / total) * 100) : 0;
    return { total, unlocked, percent };
  },[achievements]);

  const filteredAchievements = useMemo(() => {
    return achievements.filter((achievement) => {
      if (category === 'unlocked') return achievement.unlocked;
      if (category === 'locked') return !achievement.unlocked;
      return true;
    });
  },[achievements, category]);

  const handleCategoryChange = useCallback((newCategory: typeof category) => {
    setCategory(newCategory);
  },[]);

  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (stats.percent / 100) * circumference;

  return (
    <Container className="achievements" data-page="achievements">
      <Header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TitleRow>
          <TrophyIconWrapper>
            <Trophy size={28} />
          </TrophyIconWrapper>
          <Title>Достижения</Title>
        </TitleRow>
        <Subtitle>Отмечайте применения и отслеживайте свой прогресс.</Subtitle>
      </Header>

      <ControlsRow>
        <ToggleAllHints
          $active={showHints}
          onClick={() => setShowHints((v) => !v)}
          aria-pressed={showHints}
        >
          {showHints ? 'Скрыть инструкции ко всем' : 'Показать инструкции ко всем'}
        </ToggleAllHints>
      </ControlsRow>

      <ProgressSection
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <ProgressHeader>
          <ProgressTitle>Общий прогресс</ProgressTitle>
          <ProgressValue>
            {stats.unlocked}/{stats.total}
          </ProgressValue>
        </ProgressHeader>

        <CircularProgress>
          <CircularSvg width="100" height="100" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#7B68EE" />
                <stop offset="100%" stopColor="#9966CC" />
              </linearGradient>
            </defs>
            <CircularBg cx="50" cy="50" r={radius} />
            <CircularBar
              cx="50"
              cy="50"
              r={radius}
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </CircularSvg>
          <CircularText>
            <div>
              <CircularPercent>{stats.percent}%</CircularPercent>
              <CircularLabel>Открыто</CircularLabel>
            </div>
          </CircularText>
        </CircularProgress>

        <ProgressText>
          {stats.unlocked === 0
            ? '🌟 Начните применять ретинол, чтобы открыть первое достижение!'
            : stats.unlocked === stats.total
            ? '🎉 Поздравляем! Все достижения открыты!'
            : `💜 Продолжайте в том же духе! Осталось ${stats.total - stats.unlocked} достижений`}
        </ProgressText>
      </ProgressSection>

      <CategoryTabs>
        <CategoryTab
          $active={category === 'all'}
          onClick={() => handleCategoryChange('all')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Все ({stats.total})
        </CategoryTab>
        <CategoryTab
          $active={category === 'unlocked'}
          onClick={() => handleCategoryChange('unlocked')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Открыто ({stats.unlocked})
        </CategoryTab>
        <CategoryTab
          $active={category === 'locked'}
          onClick={() => handleCategoryChange('locked')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Закрыто ({stats.total - stats.unlocked})
        </CategoryTab>
      </CategoryTabs>

      {isLoading ? (
        <AchievementsGrid>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i}>
              <SkeletonRow>
                <SkeletonCircle width="56px" height="56px" />
                <SkeletonText>
                  <Skeleton width="60%" height="16px" />
                  <Skeleton width="90%" height="14px" />
                </SkeletonText>
              </SkeletonRow>
            </SkeletonCard>
          ))}
        </AchievementsGrid>
      ) : filteredAchievements.length === 0 ? (
        <EmptyState>
          <EmptyIconWrapper>
            <Trophy size={56} />
          </EmptyIconWrapper>
          <EmptyTitle>Пока пусто</EmptyTitle>
          <EmptyText>
            {category === 'unlocked'
              ? 'Открой своё первое достижение, применяя ретинол регулярно!'
              : 'Все достижения уже открыты! 🎉'}
          </EmptyText>
          {category === 'unlocked' && (
            <EmptyButton
              onClick={() => navigate('/calendar')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Calendar size={18} />
              <span>Перейти в календарь</span>
            </EmptyButton>
          )}
        </EmptyState>
      ) : (
        <AchievementsGrid>
          <AnimatePresence mode="popLayout">
            {filteredAchievements.map((achievement, index) => (
              <AchievementCardItem
                key={achievement.id}
                achievement={achievement}
                index={index}
                showHintsGlobally={showHints}
              />
            ))}
          </AnimatePresence>
        </AchievementsGrid>
      )}
    </Container>
  );
}